import type { ReactNode } from "react";

import { getLocalTimeZone, toCalendarDateTime } from "@internationalized/date";
import { formatHasCaption } from "@nlc/shared-types";
import { Delta } from "quill";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";
import { Input, TextField } from "@/components/field";
import QuillEditor from "@/components/quill-editor";
import { SegmentedControl } from "@/components/segmented-control";
import { Facebook } from "@/features/brands/create/shared/components/icons/FacebookIcon";
import { Instagram } from "@/features/brands/create/shared/components/icons/InstagramIcon";

import type { DesignFile } from "./design-step/types";
import type { PostType } from "./post-type-step/postTypes";
import type { GoLiveMode, ScheduleDraft } from "./scheduleDraft";

import { useSocialPages } from "../../hooks/useSocialPages";
import { CaptionGenerator } from "./caption-generator/CaptionGenerator";
import { PLATFORM_LABELS } from "./post-type-step/postTypes";
import { PublicationPreview } from "./PublicationPreview";
import { ScheduleDateTime } from "./ScheduleDateTime";
import { captionMaxLength, captionText } from "./scheduleDraft";

const PLATFORM_ICONS = { facebook: Facebook, instagram: Instagram };
const SECTION_CLASS =
  "flex flex-col gap-lg rounded-xl border border-border-secondary p-xl";

export function ScheduleStep({
  brandId,
  designs,
  draft,
  onChange,
  onChangeFormat,
  postType,
}: {
  brandId: number;
  designs: DesignFile[];
  draft: ScheduleDraft;
  onChange: (draft: ScheduleDraft) => void;
  onChangeFormat?: () => void;
  postType: PostType;
}) {
  const caption = captionText(draft.caption);
  const hasCaption = formatHasCaption(postType.format);
  const captionMax = captionMaxLength(postType.platform);
  const scheduledAt =
    draft.goLive === "later" && draft.date && draft.time
      ? toCalendarDateTime(draft.date, draft.time).toDate(getLocalTimeZone())
      : undefined;

  return (
    <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-3xl">
      <div className="flex flex-col gap-sm">
        <h2 className="text-display-xs font-semibold text-text-primary">
          Let&apos;s schedule your post
        </h2>
        <p className="text-md text-text-tertiary">
          You are almost there. A few quick details and it publishes on its own.
        </p>
      </div>

      <div className="flex items-start gap-3xl">
        <div className="flex min-w-0 flex-1 flex-col gap-xl">
          <ScheduleSection
            description="Only your team sees this name. It helps you find the schedule on your calendar."
            title="Name your post"
          >
            <TextField
              aria-label="Schedule name"
              maxLength={255}
              onChange={(name) => onChange({ ...draft, name })}
              value={draft.name}
            >
              <Input placeholder="e.g. Summer sale launch" />
            </TextField>
          </ScheduleSection>

          <ScheduleSection
            description="Post right away or pick a day and time. It publishes on its own."
            title="Choose when it goes live"
          >
            <SegmentedControl<GoLiveMode>
              aria-label="When it goes live"
              className="self-start"
              onChange={(goLive) => onChange({ ...draft, goLive })}
              options={[
                { id: "later", label: "Schedule for later" },
                { id: "now", label: "Post now" },
              ]}
              value={draft.goLive}
            />
            <div
              className={twMerge(
                "-mt-lg grid transition-[grid-template-rows,opacity,margin,visibility] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none",
                draft.goLive === "later"
                  ? "visible mt-0 grid-rows-[1fr] opacity-100"
                  : "invisible grid-rows-[0fr] opacity-0",
              )}
            >
              <div className="min-h-0 overflow-hidden">
                <ScheduleDateTime
                  date={draft.date}
                  onDateChange={(date) => onChange({ ...draft, date })}
                  onTimeChange={(time) => onChange({ ...draft, time })}
                  time={draft.time}
                />
              </div>
            </div>
          </ScheduleSection>

          <ScheduleSection
            description={
              onChangeFormat
                ? "You can change the format if you want something different."
                : "The page and format stay the same once a post is scheduled."
            }
            title="Check where it publishes"
          >
            <PublishTarget
              brandId={brandId}
              onChangeFormat={onChangeFormat}
              postType={postType}
            />
          </ScheduleSection>

          {hasCaption && (
            <section className={SECTION_CLASS}>
              <div className="flex items-center justify-between">
                <h3 className="text-md font-semibold text-text-primary">
                  Caption
                </h3>
                <span
                  className={twMerge(
                    "text-sm text-text-tertiary",
                    caption.length > captionMax && "text-text-error-primary",
                  )}
                >
                  {caption.length.toLocaleString()} /{" "}
                  {captionMax.toLocaleString()}
                </span>
              </div>
              <QuillEditor
                className="flex min-h-[200px] flex-col [&_.ql-container]:flex-1"
                isError={caption.length > captionMax}
                onChange={(caption) => onChange({ ...draft, caption })}
                onSelectionChange={() => {}}
                onTextChange={() => {}}
                placeholder="Write your caption, or generate one from your design."
                readOnly={false}
                value={draft.caption}
              />
              <CaptionGenerator
                brandId={brandId}
                designs={designs}
                onUseCaption={(text) =>
                  onChange({ ...draft, caption: new Delta().insert(text) })
                }
                postType={postType}
              />
            </section>
          )}
        </div>

        <aside
          aria-label="Post preview"
          className="flex w-[360px] shrink-0 flex-col gap-lg"
        >
          <h3 className="text-md font-semibold text-text-primary">Preview</h3>
          <PublicationPreview
            brandId={brandId}
            caption={caption}
            designs={designs}
            postType={postType}
            scheduledAt={scheduledAt}
          />
        </aside>
      </div>
    </div>
  );
}

function ScheduleSection({
  children,
  description,
  title,
}: {
  children: ReactNode;
  description: string;
  title: string;
}) {
  return (
    <section className={SECTION_CLASS}>
      <div className="flex flex-col gap-xs">
        <h3 className="text-md font-semibold text-text-primary">{title}</h3>
        <p className="text-sm text-text-tertiary">{description}</p>
      </div>
      {children}
    </section>
  );
}

function PublishTarget({
  brandId,
  onChangeFormat,
  postType,
}: {
  brandId: number;
  onChangeFormat?: () => void;
  postType: PostType;
}) {
  const { data } = useSocialPages();
  const page = data?.pages.find(
    (page) => page.brand.id === brandId && page.platform === postType.platform,
  );
  const PlatformIcon = PLATFORM_ICONS[postType.platform];
  const platformLabel = PLATFORM_LABELS[postType.platform];

  return (
    <div className="flex items-center gap-lg rounded-xl border border-border-secondary p-lg">
      <PlatformIcon />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-semibold text-text-primary">
          {page ? `@${page.username ?? page.name}` : platformLabel}
        </span>
        <span className="truncate text-sm text-text-tertiary">
          {platformLabel} {postType.label}
        </span>
      </div>
      {onChangeFormat && (
        <Button
          color="gray"
          onPress={onChangeFormat}
          size="sm"
          variant="secondary"
        >
          Change
        </Button>
      )}
    </div>
  );
}
